import { useEffect, useMemo, useState } from 'react';
import { ApiError, call } from '../api';
import { useApp } from '../context';
import { linkProps } from '../router';
import { LegacyStats } from './Stats';

/**
 * Kijkstatistieken uit de echte Plex-geschiedenis (zoals Tautulli): uren, activiteit door de tijd,
 * per dag/maand/uur, kijkreeksen, binge-sessies en top-titels. Valt terug op het eenvoudige
 * overzicht als de geschiedenis niet leesbaar is (ander account dan de eigenaar).
 */

interface StatsData {
  totals: { plays: number; hours: number; movies: number; episodes: number; shows: number; activeDays: number };
  byDow: number[];
  byHour: number[];
  byMonth: { month: string; count: number }[];
  streaks: { days: number; from: string; to: string }[];
  binges: { hours: number; items: number; from: number; to: number; titles: string[] }[];
  topShows: { title: string; key: string | null; thumb: string | null; episodes: number; plays: number; total: number | null }[];
  topMovies: { title: string; key: string; thumb: string | null; plays: number }[];
  recent: { title: string; key: string; at: number; type: string }[];
  people: { name: string; plays: number; hours: number }[] | null;
  scope: 'me' | 'all';
  /** Laatste 120 dagen: minuten per dag (Plex + addons) */
  daily?: Record<string, { series: number; films: number }>;
  todayHours?: number[];
  todayCounts?: { films: number; episodes: number };
}

type Period = '30d' | '90d' | '1y' | 'all';
const PERIODS: [Period, string][] = [['30d', '30 dagen'], ['90d', '90 dagen'], ['1y', '1 jaar'], ['all', 'Alles']];
const DOW = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
const MONTHS = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

function dateNl(ymdOrMs: string | number): string {
  const d = typeof ymdOrMs === 'number' ? new Date(ymdOrMs) : new Date(`${ymdOrMs}T12:00:00`);
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function Stats() {
  const { plex, me } = useApp();
  const [period, setPeriod] = useState<Period>('1y');
  const [scope, setScope] = useState<'me' | 'all'>('me');
  const [data, setData] = useState<StatsData | null>(null);
  const [legacy, setLegacy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [owner, setOwner] = useState(false);

  useEffect(() => {
    call<{ canManage: boolean }>('/api/providers').then((r) => setOwner(r.canManage)).catch(() => setOwner(false));
  }, []);

  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    call<StatsData>(`/api/stats?period=${period}&scope=${scope}`)
      .then((d) => live && setData(d))
      .catch((e: unknown) => {
        if (!live) return;
        if (e instanceof ApiError && e.status === 501) setLegacy(true);
        else setError('Statistieken laden mislukt.');
      });
    return () => {
      live = false;
    };
  }, [period, scope]);

  if (legacy) return <LegacyStats />;

  return (
    <main className="page stats-page">
      <header className="page-head">
        <h1>Kijkstatistieken</h1>
        <span className="subtle">{scope === 'all' ? 'Het hele huishouden' : me.profile?.title}</span>
      </header>
      <div className="filterbar" data-focus-group>
        {PERIODS.map(([id, label]) => (
          <button key={id} className="chip" aria-pressed={period === id} onClick={() => setPeriod(id)}>{label}</button>
        ))}
        {owner && (
          <>
            <span className="chip-sep" aria-hidden="true" />
            <button className="chip" aria-pressed={scope === 'me'} onClick={() => setScope('me')}>Mijn profiel</button>
            <button className="chip" aria-pressed={scope === 'all'} onClick={() => setScope('all')}>Huishouden</button>
          </>
        )}
      </div>

      {data?.daily && <MyOverview data={data} />}

      {error && <p className="placeholder-row error">{error}</p>}
      {!data && !error && <p className="placeholder-row" aria-busy="true">Kijkgeschiedenis doorrekenen…</p>}
      {data && data.totals.plays === 0 && <p className="placeholder-row">In deze periode nog niets gekeken.</p>}
      {data && data.totals.plays > 0 && (
        <>
          <div className="stats-tiles">
            <Tile n={data.totals.hours} label="uur gekeken" />
            <Tile n={data.totals.movies} label={data.totals.movies === 1 ? 'film' : 'films'} />
            <Tile n={data.totals.episodes} label="afleveringen" />
            <Tile n={data.totals.shows} label={data.totals.shows === 1 ? 'serie' : 'series'} />
            <Tile n={data.totals.activeDays} label="dagen gekeken" />
          </div>

          {data.people && data.people.length > 0 && (
            <section className="stats-card" aria-label="Wie kijkt het meest">
              <h2>Wie kijkt het meest</h2>
              <Bars rows={data.people.map((p) => ({ label: p.name, value: p.hours, note: `${p.hours} uur · ${p.plays}×` }))} />
            </section>
          )}

          {data.byMonth.length > 1 && (
            <section className="stats-card" aria-label="Activiteit door de tijd">
              <h2>Activiteit door de tijd</h2>
              <LineChart points={data.byMonth.map((m) => ({ label: `${MONTHS[Number(m.month.slice(5)) - 1]} ${m.month.slice(2, 4)}`, value: m.count }))} />
            </section>
          )}

          <div className="stats-cols">
            <section className="stats-card" aria-label="Per dag van de week">
              <h2>Per dag</h2>
              <Columns values={data.byDow.slice(1).concat(data.byDow[0])} labels={['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo']} />
            </section>
            <section className="stats-card" aria-label="Per maand van het jaar">
              <h2>Per maand</h2>
              <Columns values={MONTHS.map((_, i) => data.byMonth.filter((m) => Number(m.month.slice(5)) === i + 1).reduce((n, m) => n + m.count, 0))} labels={MONTHS} />
            </section>
          </div>
          <section className="stats-card" aria-label="Per uur">
            <h2>Per uur</h2>
            <Columns values={data.byHour} labels={data.byHour.map((_, h) => (h % 3 === 0 ? `${h}u` : ''))} tall />
          </section>

          <div className="stats-cols">
            <section className="stats-card" aria-label="Top series">
              <h2>Top series</h2>
              <ol className="stats-list">
                {data.topShows.slice(0, 8).map((s, i) => {
                  const pct = s.total ? Math.min(100, Math.round((s.episodes / s.total) * 100)) : null;
                  const img = plex.image(s.thumb ?? undefined, 80, 120);
                  const body = (
                    <>
                      <span className="stats-rank">{i + 1}</span>
                      {img && <img src={img} alt="" />}
                      <span className="stats-show-title">{s.title}</span>
                      <span className="stats-pill">{s.episodes}{s.total ? ` van ${s.total}` : ''} afl.{pct !== null ? ` (${pct}%)` : ''}</span>
                    </>
                  );
                  return <li key={`${s.key}-${i}`}>{s.key ? <a {...linkProps(`/item/${s.key}`)}>{body}</a> : <span>{body}</span>}</li>;
                })}
              </ol>
            </section>
            <section className="stats-card" aria-label="Top films">
              <h2>Meest gekeken films</h2>
              <ol className="stats-list">
                {data.topMovies.slice(0, 8).map((m, i) => {
                  const img = plex.image(m.thumb ?? undefined, 80, 120);
                  return (
                    <li key={m.key}>
                      <a {...linkProps(`/item/${m.key}`)}>
                        <span className="stats-rank">{i + 1}</span>
                        {img && <img src={img} alt="" />}
                        <span className="stats-show-title">{m.title}</span>
                        <span className="stats-pill">{m.plays}×</span>
                      </a>
                    </li>
                  );
                })}
              </ol>
            </section>
          </div>

          <div className="stats-cols">
            <section className="stats-card" aria-label="Kijkreeksen">
              <h2>🔥 Kijkreeksen</h2>
              {data.streaks.length === 0 && <p className="subtle">Nog geen dagen achter elkaar.</p>}
              <ul className="stats-plain">
                {data.streaks.map((s) => (
                  <li key={s.from}><strong>{s.days} dagen</strong><span className="subtle">{dateNl(s.from)} – {dateNl(s.to)}</span></li>
                ))}
              </ul>
            </section>
            <section className="stats-card" aria-label="Binge-sessies">
              <h2>🍿 Binge-sessies</h2>
              {data.binges.length === 0 && <p className="subtle">Nog geen lange kijksessies.</p>}
              <ul className="stats-plain">
                {data.binges.map((b) => (
                  <li key={b.from}>
                    <strong>{b.hours.toLocaleString('nl-NL')} uur · {b.items} titels</strong>
                    <span className="subtle">{dateNl(b.from)} · {b.titles.join(', ')}</span>
                  </li>
                ))}
              </ul>
            </section>
            <section className="stats-card" aria-label="Laatst gekeken">
              <h2>Laatst gekeken</h2>
              <ul className="stats-plain">
                {data.recent.map((r) => (
                  <li key={`${r.key}-${r.at}`}>
                    <a {...linkProps(`/item/${r.key}`)}><strong>{r.title}</strong></a>
                    <span className="subtle">{new Date(r.at).toLocaleString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </>
      )}
      <div className="footer-space" />
    </main>
  );
}

function Tile({ n, label }: { n: number; label: string }) {
  return <div className="stats-tile"><strong>{n.toLocaleString('nl-NL')}</strong><span>{label}</span></div>;
}

function Columns({ values, labels, tall = false }: { values: number[]; labels: string[]; tall?: boolean }) {
  const max = Math.max(1, ...values);
  return (
    <div className={`stats-columns ${tall ? 'is-tall' : ''}`} role="img" aria-label={labels.map((l, i) => `${l || i}: ${values[i]}`).join(', ')}>
      {values.map((v, i) => (
        <div key={i} className="stats-col" title={`${labels[i] || i}: ${v}`}>
          <span className="stats-col-bar" style={{ height: `${Math.max(2, (v / max) * 100)}%` }} />
          <span className="stats-col-label">{labels[i]}</span>
        </div>
      ))}
    </div>
  );
}

function Bars({ rows }: { rows: { label: string; value: number; note: string }[] }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className="stats-genres">
      {rows.map((r) => (
        <li key={r.label}>
          <span>{r.label}</span>
          <span className="stats-bar" aria-hidden="true"><span style={{ width: `${Math.round((r.value / max) * 100)}%` }} /></span>
          <span className="subtle">{r.note}</span>
        </li>
      ))}
    </ul>
  );
}

/** Lijngrafiek in SVG (geen extra bibliotheek nodig). */
function LineChart({ points }: { points: { label: string; value: number }[] }) {
  const W = 1000;
  const H = 220;
  const max = Math.max(1, ...points.map((p) => p.value));
  const step = points.length > 1 ? W / (points.length - 1) : W;
  const xy = points.map((p, i) => [i * step, H - (p.value / max) * (H - 20) - 10] as const);
  const line = xy.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${W},${H} L0,${H} Z`;
  const every = Math.max(1, Math.ceil(points.length / 10));
  const peak = useMemo(() => points.reduce((a, b) => (b.value > a.value ? b : a), points[0]), [points]);
  return (
    <div className="stats-line">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={`Activiteit per maand, piek ${peak.label} met ${peak.value}`}>
        <defs>
          <linearGradient id="statsFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="var(--c-accent)" stopOpacity="0.45" />
            <stop offset="100%" stopColor="var(--c-accent)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#statsFill)" />
        <path d={line} fill="none" stroke="var(--c-accent)" strokeWidth="3" vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="stats-line-labels">
        {points.map((p, i) => <span key={p.label} style={{ left: `${(i / Math.max(1, points.length - 1)) * 100}%` }}>{i % every === 0 ? p.label : ''}</span>)}
      </div>
    </div>
  );
}

export { DOW };

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function hm(min: number): string {
  if (min < 60) return `${Math.round(min)} min`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return m ? `${h}u ${m}m` : `${h} uur`;
}

/**
 * Vandaag, deze maand tegen vorige maand en de laatste 13 weken als heatmap (zoals DexHub's My-pagina).
 * Telt Plex en wat je via addons keek.
 */
function MyOverview({ data }: { data: StatsData }) {
  const daily = data.daily ?? {};
  const now = new Date();
  const today = daily[ymd(now)] ?? { series: 0, films: 0 };
  const todayMin = today.series + today.films;

  // Deze maand en vorige maand, per dag
  const month = (offset: number) => {
    const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const days = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    return Array.from({ length: days }, (_, i) => daily[ymd(new Date(first.getFullYear(), first.getMonth(), i + 1))] ?? { series: 0, films: 0 });
  };
  const thisMonth = month(0).slice(0, now.getDate());
  const lastMonth = month(-1);
  const sum = (l: { series: number; films: number }[]) => l.reduce((n, d) => n + d.series + d.films, 0);
  const lastSamePeriod = sum(lastMonth.slice(0, now.getDate()));
  const diff = lastSamePeriod > 0 ? Math.round(((sum(thisMonth) - lastSamePeriod) / lastSamePeriod) * 100) : null;
  const maxDay = Math.max(1, ...thisMonth.map((d) => d.series + d.films), ...lastMonth.map((d) => d.series + d.films));

  // 13 weken, maandag bovenaan; de laatste kolom eindigt vandaag
  const weeks: { key: string; min: number; future: boolean }[][] = [];
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - ((now.getDay() + 6) % 7) - 7 * 12);
  for (let w = 0; w < 13; w++) {
    const col: { key: string; min: number; future: boolean }[] = [];
    for (let d = 0; d < 7; d++) {
      const day = new Date(start.getFullYear(), start.getMonth(), start.getDate() + w * 7 + d);
      const v = daily[ymd(day)];
      col.push({ key: ymd(day), min: v ? v.series + v.films : 0, future: day > now });
    }
    weeks.push(col);
  }
  const level = (m: number) => (m <= 0 ? 0 : m < 45 ? 1 : m < 120 ? 2 : m < 240 ? 3 : 4);
  const monthName = now.toLocaleDateString('nl-NL', { month: 'long' });

  return (
    <div className="my-overview">
      <section className="stats-card my-today" aria-label="Vandaag">
        <h2>Vandaag</h2>
        <div className="my-today-nums">
          <span><strong>{hm(todayMin)}</strong> gekeken</span>
          <span><strong>{data.todayCounts?.films ?? 0}</strong> {data.todayCounts?.films === 1 ? 'film' : 'films'}</span>
          <span><strong>{data.todayCounts?.episodes ?? 0}</strong> afleveringen</span>
        </div>
        {todayMin > 0 && data.todayHours && <Columns values={data.todayHours} labels={data.todayHours.map((_, h) => (h % 6 === 0 ? `${h}u` : ''))} />}
      </section>

      <section className="stats-card my-month" aria-label="Deze maand tegen vorige maand">
        <h2>
          {monthName.charAt(0).toUpperCase() + monthName.slice(1)}: {hm(sum(thisMonth))}
          {diff !== null && <span className={`my-diff ${diff >= 0 ? 'is-up' : 'is-down'}`}> {diff >= 0 ? '▲' : '▼'} {Math.abs(diff)}% t.o.v. vorige maand</span>}
        </h2>
        <div className="my-month-bars" role="img" aria-label={`Per dag deze maand; vorige maand als schaduw`}>
          {lastMonth.map((prev, i) => {
            const cur = thisMonth[i];
            return (
              <div key={i} className="my-day" title={cur ? `${i + 1}: series ${hm(cur.series)}, films ${hm(cur.films)}` : `${i + 1} (vorige maand ${hm(prev.series + prev.films)})`}>
                <span className="my-prev" style={{ height: `${((prev.series + prev.films) / maxDay) * 100}%` }} />
                {cur && <span className="my-films" style={{ height: `${(cur.films / maxDay) * 100}%` }} />}
                {cur && <span className="my-series" style={{ height: `${(cur.series / maxDay) * 100}%`, bottom: `${(cur.films / maxDay) * 100}%` }} />}
              </div>
            );
          })}
        </div>
        <div className="my-legend"><span className="lg-series">Series</span><span className="lg-films">Films</span><span className="lg-prev">Vorige maand</span></div>
      </section>

      <section className="stats-card my-heat" aria-label="Laatste 13 weken">
        <h2>Laatste 13 weken</h2>
        <div className="my-heat-grid" role="img" aria-label="Kijktijd per dag in de laatste 13 weken">
          {weeks.map((col, w) => (
            <div key={w} className="my-heat-col">
              {col.map((c) => <span key={c.key} className={`my-heat-cell lvl-${c.future ? 'x' : level(c.min)}`} title={`${new Date(`${c.key}T12:00:00`).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })}: ${hm(c.min)}`} />)}
            </div>
          ))}
        </div>
        <div className="my-legend"><span>Minder</span>{[0, 1, 2, 3, 4].map((l) => <span key={l} className={`my-heat-cell lvl-${l}`} aria-hidden="true" />)}<span>Meer</span></div>
      </section>
    </div>
  );
}
